import React from "react";
import { Link, useLocation, matchPath } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

const ROUTE_MAP = [
  { pattern: "/dashboard", label: "Dashboard" },
  { pattern: "/customers", label: "Clientes" },
  { pattern: "/customers/:id", label: "Detalhes do Cliente" },
  { pattern: "/services", label: "Serviços" },
  { pattern: "/payments", label: "Pagamentos" },
  { pattern: "/balance", label: "Saldo" },
  { pattern: "/documents", label: "Documentos" },
  { pattern: "/appointments", label: "Agendamento" },
  { pattern: "/processes", label: "Acompanhamento de Processos" },
  { pattern: "/processes/:id", label: "Detalhes do Processo" },
  { pattern: "/hr/employees", label: "RH · Funcionários" },
  { pattern: "/hr/employees/:id", label: "Perfil do Funcionário" },
  { pattern: "/payroll/dashboard", label: "Folha · Painel" },
  { pattern: "/payroll/periods", label: "Folha · Competências" },
  { pattern: "/payroll/periods/:pid", label: "Detalhes da Competência" },
  { pattern: "/payroll/periods/:pid/slips/:sid", label: "Editor de Holerite" },
  { pattern: "/users", label: "Usuários" },
  { pattern: "/settings", label: "Configurações" },
  { pattern: "/settings/roles", label: "Papéis e Permissões" },
  { pattern: "/settings/audit", label: "Central de Auditoria" },
  { pattern: "/settings/system-tools", label: "Ferramentas de Sistema" },
  { pattern: "/settings/migration", label: "Migração RBAC" },
  { pattern: "/change-password", label: "Alterar senha" },
];

/**
 * AP-021 Sprint 2 · Breadcrumbs
 * Derived from the current route (segment-by-segment). Falls back to route
 * mapping for pretty labels. Home always links to /dashboard. IDs (UUIDs) in
 * URL are shown as "· <short>" to stay compact.
 */
export default function Breadcrumbs() {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "/dashboard") return null;

  const segments = pathname.split("/").filter(Boolean);
  const items = [];
  let acc = "";
  for (let i = 0; i < segments.length; i++) {
    acc += "/" + segments[i];
    // Try to find a route that matches this exact accumulated path
    let label = null;
    for (const r of ROUTE_MAP) {
      if (matchPath({ path: r.pattern, end: true }, acc)) { label = r.label; break; }
    }
    if (!label) {
      const seg = segments[i];
      // Heuristic: if segment looks like a UUID/id, render it short
      if (/^[0-9a-f-]{8,}$/i.test(seg)) {
        label = seg.length > 10 ? `${seg.slice(0, 6)}…` : seg;
      } else {
        label = seg.charAt(0).toUpperCase() + seg.slice(1);
      }
    }
    items.push({ path: acc, label, last: i === segments.length - 1 });
  }

  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="text-xs mb-3">
      <ol className="flex items-center gap-1 flex-wrap text-muted-foreground">
        <li>
          <Link to="/dashboard" className="hover:text-primary inline-flex items-center gap-1" data-testid="breadcrumb-home">
            <Home className="h-3.5 w-3.5" />
            <span className="sr-only">Início</span>
          </Link>
        </li>
        {items.map((it, idx) => (
          <li key={it.path} className="inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 opacity-50" />
            {it.last ? (
              <span className="text-foreground font-medium" data-testid={`breadcrumb-${idx}`}>{it.label}</span>
            ) : (
              <Link to={it.path} className="hover:text-primary" data-testid={`breadcrumb-${idx}`}>
                {it.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
